import { Editor } from './editor';
import { EditorManager } from './editor-manager';



export interface NodeTypeVisitor<T> {
  visitServiceBreak(el: HTMLBRElement): T;
  visitCell(id: string, editor: Editor): T;
  visitText(el: Text): T;
  visitDelimiter(el: HTMLSpanElement): T;
  visitLineBreak(br: HTMLBRElement): T;
}

export interface NodeTypeDeterminer {
  determineType<T>(node: Node, visitor: NodeTypeVisitor<T>): T;
  forEachChild(element: Element,visitor: NodeTypeVisitor<boolean | void>, filter?: (node: Node) => boolean):void;
}

export class NodeManager implements NodeTypeDeterminer {

  private readonly ALLOWED_TAG_CLASS = "allowed-tag";
  private readonly DELIMITER_CLASS = "delimiter";
  private readonly SERVICE_BR_CLASS = "service_br";

  private readonly editorManager: EditorManager;

  constructor(editorManager: EditorManager) {
    this.editorManager = editorManager;
  }

  determineType<T>(node: Node, visitor: NodeTypeVisitor<T>): T {
    switch (node.nodeType) {
      case Node.ELEMENT_NODE:
        if (this.editorManager.isEditor(node)) {
          const el = node as Element;
          return visitor.visitCell(el.id, this.editorManager.getEditor(el.id) as Editor);
        }

        if (this.isDelimiter(node)) {
          return visitor.visitDelimiter(node as HTMLSpanElement);
        }

        const el = node as Element;
        if (el.nodeName == "BR") {
          if (el.classList.contains(this.SERVICE_BR_CLASS)) {
            return visitor.visitServiceBreak(el as HTMLBRElement);
          }
          if (el.classList.contains(this.ALLOWED_TAG_CLASS)) {
            return visitor.visitLineBreak(el as HTMLBRElement);
          }
        }

        throw new Error(
          "Unexpected element node " +
          node.nodeName +
          "/" +
          node.nodeValue +
          "/" +
          (node as Element).id +
          ", update Cleaner!"
        );
      case Node.TEXT_NODE:
        return visitor.visitText(node as Text);
      default:
        throw new Error(
          "Unexpected non-element node " +
          node.nodeType +
          "/" +
          node.nodeName +
          "/" +
          node.nodeValue +
          ", update Cleaner!"
        );
    }
  }

  forEachChild(
    element: Element,
    visitor: NodeTypeVisitor<boolean | void>,
    filter?: (node: Node) => boolean
  ) {
    for (let i = 0; i < element.childNodes.length; i++) {
      const el = element.childNodes.item(i);
      if (el) {
        if (!filter || filter(el)) {
          const cont = this.determineType(el, visitor);
          if (cont === false) {
            break;
          }
        }
      }
    }
  }

  createLineBreak(): HTMLBRElement {
    const br = document.createElement("br");
    br.classList.add(this.ALLOWED_TAG_CLASS);
    return br;
  }

  createServiceLineBreak(): HTMLBRElement {
    const br = document.createElement("br");
    br.classList.add(this.SERVICE_BR_CLASS);
    return br;
  }

  createDelimiter(): HTMLSpanElement {
    const n = document.createElement("span");
    //Note: I wished to add contenteditable=false here to prevent accident insert text inside the delimiter's span
    //But unfortunately if doing so, we can't place cursor here. So, no reason to have such delimiters at all
    n.setAttribute("class", this.DELIMITER_CLASS);
    return n;
  }

  isAllowedTag(node: Node): boolean {
    const el = node as Element;
    if (el && el.classList) {
      return el.classList.contains(this.ALLOWED_TAG_CLASS);
    }
    return false;
  }

  isBreak(node:Node): boolean {
    return node.nodeName==="BR" && this.isAllowedTag(node);
  }

  isDelimiter(node: Node): boolean {
    const el = node as Element;
    if (el) {
      return (
        el.nodeName == "SPAN" && el.getAttribute("class") == this.DELIMITER_CLASS
      );
    }
    return false;
  }

}